"use client";

import { Plus } from "lucide-react";
import { useParams, useRouter } from "next/navigation";
import { TiArrowUnsorted } from "react-icons/ti";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProductFilter } from "@/hooks/use-product-filter";
import { useProductsByOrgId } from "@/hooks/use-products-by-org-id";
import { hrefPreservingPinnedChatLayout } from "@/lib/chat/chat-layout-persistence";
import { cn } from "@/lib/utils";
import {
  getProductFilterLabel,
  type ProductFilter,
} from "@/types/product-facet";

export interface ProductSelectorProps {
  readonly collapsed?: boolean;
  readonly className?: string;
}

function isFilterActive(filter: ProductFilter, option: ProductFilter): boolean {
  if (filter === option) return true;
  if (
    typeof filter === "object" &&
    typeof option === "object" &&
    filter.productId === option.productId
  ) {
    return true;
  }
  return false;
}

function menuItemClassName(active: boolean): string {
  return cn(
    "cursor-pointer rounded-sm px-2 py-1 text-sm",
    active && "bg-accent",
  );
}

const productInitialButtonClassName =
  "size-8 shrink-0 rounded-md border-0 bg-sky-600 p-0 text-white hover:bg-sky-600/90";

/** ~10 menu rows (py-1 + text-sm ≈ 28px each); remaining products scroll. */
const PRODUCT_LIST_SCROLL_CLASS =
  "max-h-[17.5rem] overflow-y-auto overscroll-contain";

function getProductInitial(label: string): string {
  return (label.trim()[0] || "P").toUpperCase();
}

export function ProductSelector({
  collapsed = false,
  className,
}: ProductSelectorProps): JSX.Element | null {
  const { orgId } = useParams();
  const router = useRouter();
  const { products, isLoading } = useProductsByOrgId();
  const { productFilter, setProductFilter } = useProductFilter();

  const filterLabel = getProductFilterLabel(productFilter, products);
  const hasProducts = products.length > 0;
  const displayLabel = hasProducts ? filterLabel : "Add a product";
  const productInitial = getProductInitial(
    hasProducts ? filterLabel : "Products",
  );

  const pushPreservingChat = (href: string): void => {
    router.push(hrefPreservingPinnedChatLayout(href));
  };

  const handleSelectFilter = (filter: ProductFilter): void => {
    setProductFilter(filter);
    if (typeof filter === "object") {
      pushPreservingChat(`/${orgId}/products/${filter.productId}?pv=grid`);
    }
  };

  const handleLabelClick = (): void => {
    if (typeof productFilter === "object") {
      pushPreservingChat(
        `/${orgId}/products/${productFilter.productId}?pv=grid`,
      );
      return;
    }
    pushPreservingChat(`/${orgId}/products`);
  };

  const menuItems = (
    <div className="p-2">
      <DropdownMenuItem
        className="cursor-pointer rounded-sm px-2 py-1 text-sm font-medium text-primary"
        onClick={() => pushPreservingChat(`/${orgId}/products/new`)}
      >
        <Plus className="mr-2 size-4" />
        New product
      </DropdownMenuItem>

      {hasProducts ? (
        <>
          <DropdownMenuSeparator className="my-2" />
          <div className={PRODUCT_LIST_SCROLL_CLASS}>
            {products.map((product) => {
              if (!product.id) return null;
              const option: ProductFilter = { productId: product.id };
              return (
                <DropdownMenuItem
                  key={product.id}
                  className={menuItemClassName(
                    isFilterActive(productFilter, option),
                  )}
                  onClick={() => handleSelectFilter(option)}
                >
                  {product.name?.trim() || "Untitled product"}
                </DropdownMenuItem>
              );
            })}
          </div>
        </>
      ) : null}

      <DropdownMenuSeparator className="my-2" />
      <DropdownMenuItem
        className="cursor-pointer rounded-sm px-2 py-1 text-sm"
        onClick={() => pushPreservingChat(`/${orgId}/products`)}
      >
        View all
      </DropdownMenuItem>
    </div>
  );

  if (collapsed) {
    return (
      <div
        id="tour-products-selector"
        className={cn("flex w-full justify-center", className)}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              title={
                hasProducts ? filterLabel : "Products — add your first product"
              }
              className={cn("mb-1", productInitialButtonClassName)}
              disabled={isLoading}
            >
              <span className="text-xs font-semibold">{productInitial}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="right"
            sideOffset={0}
            className="min-w-[250px] p-0"
          >
            {menuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div id="tour-products-selector" className={cn("mb-2 px-0", className)}>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          title={displayLabel}
          className={productInitialButtonClassName}
          disabled={isLoading}
          onClick={handleLabelClick}
        >
          <span className="text-xs font-semibold">{productInitial}</span>
        </Button>

        <div className="flex min-w-0 flex-1 items-center gap-1 rounded-md border border-white">
          <Button
            variant="ghost"
            className="flex min-w-0 flex-1 items-center justify-start rounded-md p-2 text-sm font-medium hover:bg-background/90"
            disabled={isLoading}
            onClick={handleLabelClick}
          >
            <span className="truncate text-left">{displayLabel}</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center justify-center rounded-md bg-background/40 p-2 text-sm font-medium hover:bg-background/90"
                title="Select product filter"
                disabled={isLoading}
              >
                <TiArrowUnsorted
                  className="ml-1 size-3.5 shrink-0 text-white"
                  aria-hidden
                />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="start"
              side="right"
              sideOffset={0}
              className="min-w-[250px] p-0"
            >
              {!hasProducts ? (
                <div className="px-4 py-2 text-sm text-muted-foreground">
                  Organize projects under a product structure.
                </div>
              ) : null}
              {menuItems}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
